import React, {useEffect, useState, useCallback, useMemo} from 'react';
import { useParams } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Breadcrumb from '../components/Breadcrumb';
import {
    getMatchDay,
    getAvailablePlayersForMatchDay,
    updatePlayerAvailability,
    removePlayerAvailability,
    confirmMatchDay,
    saveCourtResults,
    getCourtResults,
    sendConvocationEmails
} from '../api/matchDay';
import { formatDate } from "../utils/dateUtils";
import { useCurrentPlayer } from '../hooks/useCurrentPlayer';
import 'font-awesome/css/font-awesome.min.css';
import './MatchDayDetails.css';
import NotifyModal from '../components/NotifyModal';

export const StrictModeDroppable = ({ children, ...props }) => {
    const [enabled, setEnabled] = useState(false);
    useEffect(() => {
        const animation = requestAnimationFrame(() => setEnabled(true));
        return () => {
            cancelAnimationFrame(animation);
            setEnabled(false);
        };
    }, []);
    if (!enabled) {
        return null;
    }
    return <Droppable {...props}>{children}</Droppable>;
};

const waitingListComparator = (a, b) => {
    const pointsDiff = (b.stats?.points || 0) - (a.stats?.points || 0);
    if (pointsDiff !== 0) return pointsDiff;
    const gamesDiff = (b.stats?.gamesPlayed || 0) - (a.stats?.gamesPlayed || 0);
    if (gamesDiff !== 0) return gamesDiff;
    return (b.stats?.availability || 0) - (a.stats?.availability || 0);
};

export default function MatchDayDetails({ currentUserId, isAdmin }) {
    const { id } = useParams();
    const matchDayId = Number(id);

    const { player: currentPlayer, loading: userLoading } = useCurrentPlayer();
    const effectiveUserId = currentUserId || currentPlayer?.id || currentPlayer?.player?.id;

    const [loading, setLoading] = useState(true);
    const [matchDay, setMatchDay] = useState(null);
    const [available, setAvailable] = useState([]);
    const [selected, setSelected] = useState([]);
    const [reserves, setReserves] = useState([]);
    const [myStatus, setMyStatus] = useState('UNAVAILABLE');
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [courtScores, setCourtScores] = useState({
        0: { set1Team: '', set1Opponent: '', set2Team: '', set2Opponent: '', set3Team: '', set3Opponent: '' },
        1: { set1Team: '', set1Opponent: '', set2Team: '', set2Opponent: '', set3Team: '', set3Opponent: '' },
        2: { set1Team: '', set1Opponent: '', set2Team: '', set2Opponent: '', set3Team: '', set3Opponent: '' },
    });
    const [savedCourtResults, setSavedCourtResults] = useState([]);
    const [confirmRemovePlayer, setConfirmRemovePlayer] = useState(null);
    const [showNotifyModal, setShowNotifyModal] = useState(false);
    const [courtRuleError, setCourtRuleError] = useState(null);

    const isMatchFinished = useMemo(() => {
        if (!matchDay?.date || !matchDay?.time) return false;

        try {
            const matchDateTime = new Date(`${matchDay.date}T${matchDay.time}`);
            const now = new Date();
            const finishTime = new Date(matchDateTime.getTime() + 90 * 60000);

            return now >= finishTime;
        } catch (e) {
            return false;
        }
    }, [matchDay]);

    const load = useCallback(async () => {
        if (!currentUserId && userLoading) return;

        setLoading(true);
        try {
            const [mdData, playersDataRaw] = await Promise.all([
                getMatchDay(matchDayId),
                getAvailablePlayersForMatchDay(matchDayId)
            ]);

            setMatchDay(mdData);

            const playersData = playersDataRaw.map(item => ({
                id: item.player.id,
                name: item.player.name,
                lastName: item.player.lastName,
                position: item.player.position,
                status: item.currentStatus || 'AVAILABLE',
                stats: item.stats
            }));

            const playing = [];
            const waiting = [];
            const reserves = [];
            let currentUserStatus = 'UNAVAILABLE';

            playersData.forEach(p => {
                if (effectiveUserId && Number(p.id) === Number(effectiveUserId)) {
                    currentUserStatus = p.status;
                }

                if (p.status === 'PLAYING') playing.push(p);
                else if (p.status === 'RESERVE') reserves.push(p);
                else waiting.push(p);
            });

            setSelected(playing);
            setAvailable([...waiting].sort(waitingListComparator));
            setReserves(reserves);
            setMyStatus(currentUserStatus);

        } catch (e) {
            console.error(e);
            setError('No se pudo cargar la información del partido.');
        } finally {
            setLoading(false);
        }
    }, [matchDayId, effectiveUserId, userLoading, currentUserId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!matchDayId) return;
        getCourtResults(matchDayId).then(results => {
            if (!results || results.length === 0) return;
            setSavedCourtResults(results);
            const newScores = { ...courtScores };
            results.forEach(r => {
                const idx = r.courtNumber - 1;
                if (idx >= 0 && idx <= 2) {
                    newScores[idx] = {
                        set1Team:     r.set1Team     ?? '',
                        set1Opponent: r.set1Opponent ?? '',
                        set2Team:     r.set2Team     ?? '',
                        set2Opponent: r.set2Opponent ?? '',
                        set3Team:     r.set3Team     ?? '',
                        set3Opponent: r.set3Opponent ?? '',
                    };
                }
            });
            setCourtScores(newScores);
        }).catch(() => {});
    }, [matchDayId]);

    const handleBecomeAvailable = async () => {
        if (!effectiveUserId) return;
        setActionLoading(true);
        try {
            await updatePlayerAvailability(effectiveUserId, matchDayId, 'AVAILABLE');
            await load();
        } catch (e) { setError('Error al apuntarse.'); } finally { setActionLoading(false); }
    };

    const handleBecomeUnavailable = async () => {
        if (!effectiveUserId) return;
        setActionLoading(true);
        try {
            await removePlayerAvailability(effectiveUserId, matchDayId);
            setMyStatus('UNAVAILABLE');
            await load();
        } catch (e) { setError('Error al darse de baja.'); } finally { setActionLoading(false); }
    };

    const handleRemovePlayerAvailability = async () => {
        if (!confirmRemovePlayer) return;
        try {
            await removePlayerAvailability(confirmRemovePlayer.id, matchDayId);
            setConfirmRemovePlayer(null);
            await load();
        } catch (e) {
            setError('Error al quitar la disponibilidad de la jugadora.');
            setConfirmRemovePlayer(null);
        }
    }

    const handleRemovePlayerClick = (player) => {
        setConfirmRemovePlayer(player);
    }

    const getGlobalIndex = (droppableId, index) => {
        if (droppableId === 'available') return null;
        const courtIndex = parseInt(droppableId.replace('court-', ''));
        return (courtIndex * 2) + index;
    };

    const onDragEnd = (result) => {
        if (!isAdmin) return;
        const { source, destination } = result;

        if (!destination) return;
        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        const sourceIsAvailable = source.droppableId === 'available';
        const destIsAvailable = destination.droppableId === 'available';

        let newAvailable = [...available];
        let newSelected = [...selected];
        let newReserves = [...reserves];

        let movedPlayer;

        if (sourceIsAvailable) {
            [movedPlayer] = newAvailable.splice(source.index, 1);
        } else if (source.droppableId === 'reserves') {
            [movedPlayer] = newReserves.splice(source.index, 1);
        } else if (source.droppableId.startsWith('court-')) {
            const sourceIndex = getGlobalIndex(source.droppableId, source.index);
            [movedPlayer] = newSelected.splice(sourceIndex, 1);
        }

        if (!movedPlayer) return;

        if (destination.droppableId === 'available') {
            newAvailable.splice(destination.index, 0, movedPlayer);
        } else if (destination.droppableId === 'reserves') {
            newReserves.splice(destination.index, 0, movedPlayer);
        } else if (destination.droppableId.startsWith('court-')) {
            const destIndex = getGlobalIndex(destination.droppableId, destination.index);
            if (destIndex >= newSelected.length) {
                newSelected.push(movedPlayer);
            } else {
                newSelected.splice(destIndex, 0, movedPlayer);
            }
        }

        if (destination.droppableId.startsWith('court-') || source.droppableId.startsWith('court-')) {
            const ruleError = validateCourtRule(newSelected);
            if (ruleError) {
                setCourtRuleError(ruleError);
                return;
            }
        }

        setCourtRuleError(null);
        setAvailable([...newAvailable].sort(waitingListComparator));
        setSelected(newSelected);
        setReserves(newReserves);
    };

    const handleAutoSelect = () => {
        const allPlayers = [...selected, ...available];
        if (allPlayers.length < 6) {
            setError('No hay suficientes jugadores para llenar 3 pistas.');
            return;
        }

        allPlayers.sort((a, b) => {
            const matchesA = a.stats?.gamesPlayed || 0;
            const matchesB = b.stats?.gamesPlayed || 0;

            if (matchesA !== matchesB) return matchesA - matchesB;

            return (b.stats?.availability || 0) - (a.stats?.availability || 0);
        });

        const top6Candidates = allPlayers.slice(0, 6);
        const remaining = allPlayers.slice(6);

        top6Candidates.sort((a, b) => (b.stats?.points || 0) - (a.stats?.points || 0));

        const rawPairs = [
            [top6Candidates[0], top6Candidates[5]],
            [top6Candidates[1], top6Candidates[4]],
            [top6Candidates[2], top6Candidates[3]]
        ];

        const getPairSum = (pair) => (pair[0].stats?.points || 0) + (pair[1].stats?.points || 0);
        rawPairs.sort((pA, pB) => getPairSum(pB) - getPairSum(pA));

        setSelected(rawPairs.flat());
        setAvailable(remaining);
    }

    const handleSaveConvocation = async () => {
        setSaving(true);
        try {
            const playedPlayerIds = selected.map(p => p.id).filter(Boolean);
            const reservePlayerIds = reserves.map(p => p.id).filter(Boolean);
            const availableIds = available.map(p => p.id).filter(Boolean);
            const allConfirmedPlayers = [...playedPlayerIds, ...availableIds, ...reservePlayerIds];

            await confirmMatchDay(matchDayId, allConfirmedPlayers, playedPlayerIds, reservePlayerIds, []);
            await load();
            setShowNotifyModal(true);
        } catch (e) {
            console.error(e);
            setError('Error al confirmar el partido.');
        } finally {
            setSaving(false);
        }
    };

    const handleSendNotifications = async (playerIds) => {
        setShowNotifyModal(false);
        if (playerIds.length === 0) return;
        try {
            await sendConvocationEmails(matchDayId, playerIds);
        } catch (e) {
            setError('Error al enviar las notificaciones.');
        }
    };

    const handleProcessResults = async () => {
        setSaving(true);
        try {
            const courts = getCourts(selected);
            const courtResults = [];

            [0, 1, 2].forEach(courtIndex => {
                const playersInCourt = courts[courtIndex] || [];
                if (playersInCourt.length === 0) return;

                const scores = courtScores[courtIndex];
                const toInt = v => (v === '' || v === null || v === undefined) ? null : parseInt(v, 10);

                courtResults.push({
                    courtNumber:  courtIndex + 1,
                    set1Team:     toInt(scores.set1Team),
                    set1Opponent: toInt(scores.set1Opponent),
                    set2Team:     toInt(scores.set2Team),
                    set2Opponent: toInt(scores.set2Opponent),
                    set3Team:     toInt(scores.set3Team),
                    set3Opponent: toInt(scores.set3Opponent),
                });
            });

            await saveCourtResults(matchDayId, courtResults);
            await load();
        } catch (e) {
            setError('Error al procesar los resultados.');
        } finally {
            setSaving(false);
        }
    }

    const getStatusClass = () => {
        if (myStatus === 'PLAYING') return 'playing';
        if (myStatus === 'RESERVE') return 'reserve';
        if (myStatus === 'AVAILABLE') return 'available';
        return 'unavailable';
    };

    const getInitials = (p) => (p.name ? p.name[0] : '?').toUpperCase();

    const getCourts = (players) => {
        const courts = [[], [], []];
        players.forEach((p, index) => {
            const courtIndex = Math.floor(index / 2);
            if (courtIndex < 3) courts[courtIndex].push(p);
        });
        return courts;
    };

    const getCourtPoints = (players) =>
        getCourts(players).map(court =>
            court.reduce((sum, p) => sum + (p.stats?.points || 0), 0)
        );

    const validateCourtRule = (newSelected) => {
        const [pts1, pts2, pts3] = getCourtPoints(newSelected);
        if (pts1 < pts2) return 'La pista 1 no puede tener menos puntos que la pista 2.';
        if (pts2 < pts3) return 'La pista 2 no puede tener menos puntos que la pista 3.';
        return null;
    }

    const getPosLetter = (pos) => {
        if (!pos) return '-';
        const p = pos.toLowerCase();
        if (p.includes('der')) return 'D';
        if (p.includes('izq') || p.includes('rev')) return 'R';
        if (p.includes('amb')) return 'A';
        return '-';
    };

    const handleShareWhatsApp = () => {
        const dateStr = matchDay?.date ? formatDate(matchDay.date) : 'Por definir';
        const matchDayId = matchDay?.id || id;
        const matchUrl = `https://www.ligacuartapg.es/matchdays/${matchDayId}`;

        const iAnnounce = "\u{1F4E2}"; // 📢
        const iCalendar = "\u{1F5D3}"; // 🗓️
        const iLocation = "\u{1F4CD}"; // 📍
        const iTennis = "\u{1F3BE}";   // 🎾
        const iCheck = "\u{2705}";     // ✅
        const iFire = "\u{1F525}";      // 🔥
        const iLink = "\u{1F517}";      // 🔗
        const iClock = "\u{23F3}";      // ⏳

        let message = "";

        if (selected.length === 0) {
            message = `${iAnnounce} *RECORDATORIO PARTIDO ABIERTO*\n\n`;
            message += `Hola equipo, se ha abierto la convocatoria para el próximo partido:\n\n`;
            message += `${iCalendar} *Fecha:* ${dateStr} - ${matchDay?.time || ''}\n`;
            message += `${iTennis} *Rival:* ${matchDay?.opponentName || 'Jornada Interna'}\n`;
            message += `${iLocation} *Lugar:* ${matchDay?.location || 'Por confirmar'}\n\n`;
            message += `${iClock} *Por favor, entrad en la web y marcad si estáis disponibles para poder organizar las pistas.* \n\n`;
            message += `${iLink} *Apúntate aquí:* ${matchUrl}\n`;
        } else {
            const courts = getCourts(selected);
            message = `${iAnnounce} *CONVOCATORIA PÁDEL FLINTA*\n\n`;
            message += `${iCalendar} *Fecha:* ${dateStr} - ${matchDay?.time || ''}\n`;
            message += `${iLocation} *Lugar:* ${matchDay?.location || 'Por confirmar'}\n`;
            message += `${iTennis} *Rival:* ${matchDay?.opponentName || 'Jornada Interna'}\n\n`;

            message += `*JUGADORAS CONVOCADAS:*\n`;

            courts.forEach((court, i) => {
                if (court.length > 0) {
                    message += `\n*Pista ${i + 1}:*\n`;
                    court.forEach(p => {
                        message += `• ${p.name} ${p.lastName}\n`;
                    });
                }
            });

            if (reserves.length > 0) {
                message += `\n*RESERVAS:*\n`;
                reserves.forEach(p => {
                    message += `• ${p.name} ${p.lastName} (Reserva)\n`;
                });
            }

            message += `\n${iLink} *Detalles:* ${matchUrl}\n`;
            message += `\n${iCheck} ¡Mucha suerte equipo! ${iTennis}${iFire}`;
        }

        const encodedMessage = encodeURIComponent(message);

        window.open(`https://api.whatsapp.com/send?text=${encodedMessage}`, '_blank');
    };

    const isConvocationClosed = useMemo(() => {
        return selected.length > 0 || reserves.length > 0;
    }, [selected, reserves]);

    if (loading || userLoading) return (
        <div className="match-details-wrapper">
            <div className="loading-wrapper"><div className="spinner"></div><p>Cargando partido...</p></div>
        </div>
    );

    if (!effectiveUserId) return (
        <div className="match-details-wrapper">
            <div className="error-banner">No se ha podido identificar al usuario.</div>
        </div>
    );

    return (
        <div className="match-details-wrapper">
            <div className="match-details-container">

                <header className="match-header">
                    <Breadcrumb crumbs={[
                        { label: 'Inicio', to: '/dashboard' },
                        { label: matchDay?.opponentName ? `vs ${matchDay.opponentName}` : `Jornada ${matchDayId}` },
                    ]} />
                    <span className="match-subtitle">Gestión de Partido</span>
                    <h1 className="match-title">{matchDay?.opponentName ? `vs ${matchDay.opponentName}` : `Jornada ${matchDayId}`}</h1>

                    <div className="meta-row">
                        <div className="meta-item">
                            <i className="fa fa-calendar"></i>
                            {matchDay?.date ? `${formatDate(matchDay.date)} - ${matchDay.time}` : 'Fecha y hora desconocidas'}
                        </div>
                        {matchDay?.location && (
                            <div className="meta-item">
                                <i className="fa fa-map-marker"></i>
                                {matchDay.location}
                            </div>
                        )}
                    </div>
                </header>

                {error && <div className="error-banner"><i className="fa fa-exclamation-triangle"></i> {error}</div>}

                <div className={`status-card ${getStatusClass()}`}>
                    <div className="status-info">
                        <h3>¿Vas a jugar este partido?</h3>
                        <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
                            <span style={{fontSize:'0.9rem', color:'#666'}}>Estado actual:</span>
                            <span className={`status-badge ${getStatusClass()}`}>
                                {myStatus === 'PLAYING' ? 'Convocado/a' :
                                    myStatus === 'RESERVE' ? 'Reserva' :
                                    myStatus === 'AVAILABLE' ? 'Disponible' : 'No Disponible'}
                            </span>
                        </div>
                    </div>

                    <div className="status-actions">
                        {myStatus === 'UNAVAILABLE' ? (
                            <button
                                className="btn-action primary"
                                onClick={handleBecomeAvailable}
                                disabled={actionLoading || isConvocationClosed}
                            >
                                <i className="fa fa-check-circle"></i>
                                {isConvocationClosed ? 'Convocatoria cerrada' : '¡Me apunto!'}
                            </button>
                        ) : (
                            <button className="btn-action danger" onClick={handleBecomeUnavailable} disabled={actionLoading || myStatus === 'PLAYING'}>
                                <i className="fa fa-times-circle"></i> {myStatus === 'PLAYING' ? 'Contactar Capitana' : 'Baja'}
                            </button>
                        )}
                    </div>
                </div>

                {isAdmin ? (
                    <div className="content-card">
                        <div className="card-header" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                            <div>
                                <h2>Gestor de Pistas</h2>
                                <p>Arrastra jugadoras a las pistas (máximo 2 por pista).</p>
                            </div>
                            <div className="admin-actions">
                                <button className="btn-action" onClick={handleAutoSelect} disabled={selected.length >= 6 || available.length === 0}>
                                    <i className="fa fa-magic"></i> Auto
                                </button>
                                <button className="btn-action primary" onClick={handleSaveConvocation} disabled={saving}>
                                    {saving ? 'Guardando...' : 'Confirmar'}
                                </button>
                                <button
                                    className="btn-action"
                                    onClick={handleShareWhatsApp}
                                    style={{
                                        backgroundColor: '#25D366',
                                        color: 'white',
                                        border: 'none',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        fontWeight: '700'
                                    }}
                                    disabled={saving}
                                >
                                    <i className="fa fa-whatsapp" style={{ fontSize: '1.2rem' }}></i>
                                    {selected.length === 0 ? 'Enviar Recordatorio' : 'Compartir Convocatoria'}
                                </button>
                                {isMatchFinished && (
                                    <button className="btn-action"
                                            onClick={handleProcessResults}
                                            disabled={saving || selected.length === 0}
                                            style={{backgroundColor: '#16a34a', color: 'white', border: 'none', boxShadow: '0 4px 10px rgba(22, 163, 74, 0.3)'}}>
                                        <i className="fa fa-trophy"></i> Cerrar Acta
                                    </button>
                                )}
                            </div>
                        </div>
                        {!isMatchFinished && (
                            <div style={{
                                padding: '10px 15px',
                                background: '#FFF7ED',
                                border: '1px solid #FFEDD5',
                                borderRadius: '8px',
                                color: '#C2410C',
                                fontSize: '0.85rem',
                                marginBottom: '15px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <i className="fa fa-clock-o"></i>
                                <span>La opción de "Cerrar Acta" estará disponible al finalizar el partido ({matchDay?.time}).</span>
                            </div>
                        )}
                        {courtRuleError && (
                            <div style={{
                                display: 'flex', alignItems: 'center', gap: '10px',
                                background: '#FEF2F2', border: '1px solid #FECACA',
                                borderRadius: '10px', padding: '12px 16px', marginBottom: '16px',
                                color: '#991B1B', fontSize: '0.9rem', fontWeight: '500'
                            }}>
                                <i className="fa fa-exclamation-triangle" style={{ color: '#EF4444' }} />
                                <span>{courtRuleError}</span>
                                <button
                                    onClick={() => setCourtRuleError(null)}
                                    style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', fontSize: '1rem' }}
                                >
                                    <i className="fa fa-times" />
                                </button>
                            </div>
                        )}
                        <DragDropContext onDragEnd={onDragEnd}>
                            <div style={{display: 'flex', gap: '20px', flexDirection: window.innerWidth < 900 ? 'column' : 'row'}}>

                                <div style={{flex: 2, display: 'flex', flexDirection: 'column', gap: '15px'}}>
                                    {[0, 1, 2].map(courtIndex => {
                                        const courtPlayers = getCourts(selected)[courtIndex] || [];
                                        const currentPoints = courtPlayers.reduce((acc, p) => acc + (p.stats?.points || 0), 0);

                                        return (
                                            <div key={courtIndex} className="court-card" style={{background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px'}}>
                                                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px', fontSize:'0.9rem', fontWeight:'700', color:'#475569'}}>

                                                    <div style={{display:'flex', alignItems:'center', gap:'12px'}}>
                                                        <span>PISTA {courtIndex + 1}</span>

                                                        {isMatchFinished && (() => {
                                                            const s = courtScores[courtIndex];
                                                            const t1 = parseInt(s.set1Team), o1 = parseInt(s.set1Opponent);
                                                            const t2 = parseInt(s.set2Team), o2 = parseInt(s.set2Opponent);
                                                            const t3 = parseInt(s.set3Team), o3 = parseInt(s.set3Opponent);
                                                            let setsWon = 0, setsLost = 0;
                                                            if (!isNaN(t1) && !isNaN(o1)) { t1 > o1 ? setsWon++ : setsLost++; }
                                                            if (!isNaN(t2) && !isNaN(o2)) { t2 > o2 ? setsWon++ : setsLost++; }
                                                            if (!isNaN(t3) && !isNaN(o3)) { t3 > o3 ? setsWon++ : setsLost++; }
                                                            const won = setsWon > setsLost && setsWon > 0;
                                                            const lost = setsLost > setsWon && setsLost > 0;
                                                            return (
                                                                <span style={{
                                                                    fontSize:'0.75rem', fontWeight:'700',
                                                                    padding: '3px 8px', borderRadius: '6px',
                                                                    backgroundColor: won ? '#DCFCE7' : lost ? '#FEE2E2' : '#F1F5F9',
                                                                    color: won ? '#166534' : lost ? '#991B1B' : '#64748B',
                                                                    border: won ? '1px solid #86EFAC' : lost ? '1px solid #FCA5A5' : '1px solid #CBD5E1',
                                                                }}>
                                                                    {won ? '✓ Ganada' : lost ? '✗ Perdida' : '— Resultado'}
                                                                </span>
                                                            );
                                                        })()}
                                                    </div>

                                                    <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
                                                        <span style={{fontSize:'0.8rem', background:'#E2E8F0', padding:'2px 8px', borderRadius:'10px', color:'#475569'}}>
                                                            {currentPoints} pts
                                                        </span>
                                                        <span style={{fontSize:'0.8rem', fontWeight:'normal', color: courtPlayers.length === 2 ? '#10B981' : '#94A3B8'}}>
                                                            {courtPlayers.length}/2
                                                        </span>
                                                    </div>
                                                </div>

                                                <StrictModeDroppable droppableId={`court-${courtIndex}`}>
                                                    {(provided, snapshot) => (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.droppableProps}
                                                            style={{
                                                                minHeight: '80px',
                                                                background: snapshot.isDraggingOver ? '#F0FDF4' : 'white',
                                                                border: snapshot.isDraggingOver ? '1px dashed #166534' : '1px dashed #CBD5E1',
                                                                borderRadius: '8px',
                                                                padding: '8px',
                                                                transition: 'all 0.2s ease'
                                                            }}
                                                        >
                                                            {courtPlayers.map((p, index) => (
                                                                <Draggable key={p.id} draggableId={String(p.id)} index={index}>
                                                                    {(provided) => (
                                                                        <div
                                                                            ref={provided.innerRef}
                                                                            {...provided.draggableProps}
                                                                            {...provided.dragHandleProps}
                                                                            className="player-chip selected"
                                                                            style={{
                                                                                width: '100%', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                                                backgroundColor: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', border: '1px solid #BBF7D0',
                                                                                ...provided.draggableProps.style
                                                                            }}
                                                                        >
                                                                            <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                                                                <div className="avatar-mini" style={{backgroundColor:'#10B981'}}>{getInitials(p)}</div>
                                                                                <div style={{display:'flex', flexDirection:'column', lineHeight:'1.1'}}>
                                                                                    <span style={{fontWeight:'500'}}>{p.name} {p.lastName}</span>
                                                                                    <span style={{fontSize:'0.65rem', color:'#64748B'}}>
                                                                                        {p.stats?.gamesPlayed|| 0} part. | {p.stats?.availability || 0} disp.
                                                                                        {p.position && (
                                                                                            <b style={{ marginLeft: '6px', color: '#1e293b', background: '#e2e8f0', padding: '1px 4px', borderRadius: '3px' }}>
                                                                                                {getPosLetter(p.position)}
                                                                                            </b>
                                                                                        )}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                            <div style={{fontSize:'0.85rem', color:'#059669', fontWeight:'700'}}>
                                                                                {p.stats?.points || 0} pts
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </Draggable>
                                                            ))}
                                                            {provided.placeholder}
                                                        </div>
                                                    )}
                                                </StrictModeDroppable>

                                                {/* Inputs de resultado por sets */}
                                                {isMatchFinished && (
                                                    <div style={{marginTop: '12px', borderTop: '1px solid #E2E8F0', paddingTop: '12px'}}>
                                                        <div style={{fontSize: '0.72rem', fontWeight: '700', color: '#64748B', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                                                            Resultado (Panteres Grogues – {matchDay?.opponentName || 'Rival por definir'})
                                                        </div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'auto repeat(3, 1fr)', gap: '4px 8px', alignItems: 'center' }}>
                                                            {/* Cabecera */}
                                                            <div />
                                                            {['Set 1', 'Set 2', 'Set 3*'].map(h => (
                                                                <div key={h} style={{ fontSize: '0.7rem', fontWeight: '700', color: '#94A3B8', textAlign: 'center' }}>{h}</div>
                                                            ))}
                                                            {/* Fila PG */}
                                                            <span style={{ fontSize: '0.72rem', color: '#475569', fontWeight: '600', paddingRight: '4px' }}>PG</span>
                                                            {['set1Team', 'set2Team', 'set3Team'].map(key => (
                                                                <input
                                                                    key={key}
                                                                    type="number" min="0" max="99"
                                                                    placeholder="0"
                                                                    value={courtScores[courtIndex][key]}
                                                                    onChange={e => setCourtScores(prev => ({
                                                                        ...prev,
                                                                        [courtIndex]: { ...prev[courtIndex], [key]: e.target.value }
                                                                    }))}
                                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #CBD5E1', borderRadius: '6px', padding: '4px 2px', fontSize: '0.85rem', fontWeight: '700' }}
                                                                />
                                                            ))}
                                                            {/* Fila Rival */}
                                                            <span style={{ fontSize: '0.72rem', color: '#475569', fontWeight: '600', paddingRight: '4px' }}>Rival</span>
                                                            {['set1Opponent', 'set2Opponent', 'set3Opponent'].map(key => (
                                                                <input
                                                                    key={key}
                                                                    type="number" min="0" max="99"
                                                                    placeholder="0"
                                                                    value={courtScores[courtIndex][key]}
                                                                    onChange={e => setCourtScores(prev => ({
                                                                        ...prev,
                                                                        [courtIndex]: { ...prev[courtIndex], [key]: e.target.value }
                                                                    }))}
                                                                    style={{ width: '100%', textAlign: 'center', border: '1px solid #CBD5E1', borderRadius: '6px', padding: '4px 2px', fontSize: '0.85rem', fontWeight: '700' }}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}

                                    <div className="court-card" style={{background: '#FFFBEB', border: '2px solid #FEF08A', borderRadius: '12px', padding: '16px'}}>
                                        <div style={{display:'flex', justifyContent:'space-between', marginBottom:'10px', fontWeight:'700', color:'#854D0E'}}>
                                            <span><i className="fa fa-clock-o"></i> RESERVAS</span>
                                            <span style={{fontSize:'0.8rem'}}>{reserves.length}/2</span>
                                        </div>
                                        <StrictModeDroppable droppableId="reserves">
                                            {(provided, snapshot) => (
                                                <div ref={provided.innerRef} {...provided.droppableProps} style={{minHeight: '60px', background: snapshot.isDraggingOver ? '#FEFCE8' : 'white', border: '1px dashed #FDE68A', borderRadius: '8px', padding: '8px'}}>
                                                    {reserves.map((p, index) => (
                                                        <Draggable key={p.id} draggableId={String(p.id)} index={index}>
                                                            {(provided) => (
                                                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="player-chip selected" style={{borderColor: '#FDE68A', ...provided.draggableProps.style}}>
                                                                    <div style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                                                        <div className="avatar-mini" style={{backgroundColor:'#F59E0B'}}>{getInitials(p)}</div>
                                                                        <span>{p.name} {p.lastName}</span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </StrictModeDroppable>
                                    </div>
                                </div>

                                <div style={{flex: 1, background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #E2E8F0', height: 'fit-content'}}>
                                    <h3 className="players-section-title">EN ESPERA ({available.length})</h3>

                                    <StrictModeDroppable droppableId="available">
                                        {(provided, snapshot) => (
                                            <div
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                                style={{
                                                    minHeight: '200px',
                                                    background: snapshot.isDraggingOver ? '#F8FAFC' : 'transparent',
                                                    borderRadius: '8px',
                                                    transition: 'background 0.2s'
                                                }}
                                            >
                                                {available.length === 0 && <p style={{fontStyle:'italic', color:'#999', fontSize:'0.9rem', textAlign:'center', marginTop:'20px'}}>Lista vacía</p>}
                                                {available.map((p, index) => (
                                                    <Draggable key={p.id} draggableId={String(p.id)} index={index}>
                                                        {(provided) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className="player-chip"
                                                                style={{
                                                                    width: '100%', marginBottom: '8px', background: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                                    ...provided.draggableProps.style
                                                                }}
                                                            >
                                                                <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
                                                                    <div className="avatar-mini" style={{background:'#CBD5E1'}}>{getInitials(p)}</div>
                                                                    <div style={{display:'flex', flexDirection:'column', lineHeight:'1.1'}}>
                                                                        <span>{p.name} {p.lastName}</span>
                                                                        <span style={{fontSize:'0.65rem', color:'#94A3B8'}}>
                                                                             {p.stats?.gamesPlayed || 0} part. | {p.stats?.availability || 0} disp. | {getPosLetter(p.position)} pos.
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <span style={{fontSize:'0.75rem', color:'#64748B', fontWeight:'600'}}>{p.stats?.points || 0} pts</span>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleRemovePlayerClick(p);
                                                                    }}
                                                                    title="Quitar disponibilidad"
                                                                    style={{
                                                                        background: 'none', border: 'none', cursor: 'pointer',
                                                                        color: '#EF4444', padding: '2px 4px', borderRadius: '4px',
                                                                        fontSize: '0.85rem', lineHeight: 1
                                                                    }}
                                                                >
                                                                    <i className="fa fa-times-circle" />
                                                                </button>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </StrictModeDroppable>
                                </div>
                            </div>
                        </DragDropContext>
                    </div>
                ) : (
                    <div className="content-card">
                        <div className="card-header">
                            <h2>Convocadas</h2>
                            <p>{available.length + selected.length} jugadoras disponibles para este partido.</p>
                        </div>

                        <div className="courts-grid" style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px'}}>
                            {getCourts(selected).map((courtPlayers, i) => {
                                const pairPoints = courtPlayers.reduce((acc, p) => acc + (p.stats?.points || 0), 0);
                                const s = courtScores[i];
                                const t1 = parseInt(s.set1Team), o1 = parseInt(s.set1Opponent);
                                const t2 = parseInt(s.set2Team), o2 = parseInt(s.set2Opponent);
                                const t3 = parseInt(s.set3Team), o3 = parseInt(s.set3Opponent);
                                const hasResults = !isNaN(t1) && !isNaN(o1);
                                let setsWon = 0, setsLost = 0;
                                if (!isNaN(t1) && !isNaN(o1)) { t1 > o1 ? setsWon++ : setsLost++; }
                                if (!isNaN(t2) && !isNaN(o2)) { t2 > o2 ? setsWon++ : setsLost++; }
                                if (!isNaN(t3) && !isNaN(o3)) { t3 > o3 ? setsWon++ : setsLost++; }
                                const courtWon = setsWon > setsLost && setsWon > 0;
                                const courtLost = setsLost > setsWon && setsLost > 0;

                                return (
                                    <div key={i} className="court-card" style={{
                                        background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
                                    }}>
                                        <div style={{display:'flex', justifyContent:'space-between', fontSize:'0.85rem', fontWeight:'700', color:'#64748B', marginBottom:'12px', borderBottom:'1px solid #f1f5f9', paddingBottom:'8px'}}>
                                            <span style={{display:'flex', alignItems:'center', gap:'8px'}}>
                                                PISTA {i + 1}
                                                {isMatchFinished && hasResults && (
                                                    <span style={{
                                                        fontSize:'0.72rem', fontWeight:'700',
                                                        padding: '2px 7px', borderRadius: '6px',
                                                        backgroundColor: courtWon ? '#DCFCE7' : courtLost ? '#FEE2E2' : '#F1F5F9',
                                                        color: courtWon ? '#166534' : courtLost ? '#991B1B' : '#64748B',
                                                        border: courtWon ? '1px solid #86EFAC' : courtLost ? '1px solid #FCA5A5' : '1px solid #CBD5E1',
                                                    }}>
                                                        {courtWon ? '✓ Ganada' : courtLost ? '✗ Perdida' : '—'}
                                                    </span>
                                                )}
                                            </span>
                                            <span title="Suma total de puntos" style={{background:'#F1F5F9', padding:'2px 6px', borderRadius:'4px'}}>{pairPoints} pts</span>
                                        </div>
                                        <div style={{display:'flex', flexDirection:'column', gap:'8px'}}>
                                            {courtPlayers.length === 0 && <span style={{fontSize:'0.8rem', color:'#ccc', fontStyle:'italic'}}>Vacío</span>}
                                            {courtPlayers.map(p => (
                                                <div key={p.id} className="player-chip selected" style={{width:'100%', justifyContent:'flex-start', margin: 0}}>
                                                    <div className="avatar-mini">{getInitials(p)}</div>
                                                    <div style={{display:'flex', flexDirection:'column', lineHeight:'1.2'}}>
                                                        <span style={{fontWeight:600}}>{p.name} {p.lastName}</span>
                                                        <span style={{fontSize:'0.7rem', color:'#166534', opacity:0.8}}>{p.stats?.gamesPlayed || 0} part. | {p.stats?.points || 0} pts</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {isMatchFinished && hasResults && (
                                            <div style={{marginTop:'12px', borderTop:'1px solid #E2E8F0', paddingTop:'12px'}}>
                                                <div style={{fontSize:'0.7rem', fontWeight:'700', color:'#94A3B8', marginBottom:'8px', textTransform:'uppercase', letterSpacing:'0.05em'}}>
                                                    Resultado (PG – {matchDay?.opponentName || 'Rival'})
                                                </div>
                                                <div style={{display:'grid', gridTemplateColumns:'auto repeat(3, 1fr)', gap:'4px 8px', alignItems:'center'}}>
                                                    <div/>
                                                    {['Set 1', 'Set 2', 'Set 3'].map(h => (
                                                        <div key={h} style={{fontSize:'0.68rem', fontWeight:'700', color:'#94A3B8', textAlign:'center'}}>{h}</div>
                                                    ))}
                                                    <span style={{fontSize:'0.72rem', color:'#475569', fontWeight:'600'}}>PG</span>
                                                    {[s.set1Team, s.set2Team, s.set3Team].map((val, idx) => (
                                                        <div key={idx} style={{textAlign:'center', fontSize:'0.9rem', fontWeight:'700', color:'#1e293b',
                                                            background:'#F8FAFC', borderRadius:'6px', padding:'4px 2px',
                                                            border: idx < 2 || !isNaN(parseInt(val)) ? '1px solid #E2E8F0' : 'none',
                                                            opacity: (idx === 2 && isNaN(parseInt(val))) ? 0.3 : 1
                                                        }}>
                                                            {isNaN(parseInt(val)) ? '–' : val}
                                                        </div>
                                                    ))}
                                                    <span style={{fontSize:'0.72rem', color:'#475569', fontWeight:'600'}}>Rival</span>
                                                    {[s.set1Opponent, s.set2Opponent, s.set3Opponent].map((val, idx) => (
                                                        <div key={idx} style={{textAlign:'center', fontSize:'0.9rem', fontWeight:'700', color:'#1e293b',
                                                            background:'#F8FAFC', borderRadius:'6px', padding:'4px 2px',
                                                            border: idx < 2 || !isNaN(parseInt(val)) ? '1px solid #E2E8F0' : 'none',
                                                            opacity: (idx === 2 && isNaN(parseInt(val))) ? 0.3 : 1
                                                        }}>
                                                            {isNaN(parseInt(val)) ? '–' : val}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {reserves.length > 0 && (
                            <div style={{marginTop: '24px', padding: '16px', background: '#FFFBEB', borderRadius: '12px', border: '1px solid #FEF08A'}}>
                                <h3 style={{fontSize:'1rem', color:'#854D0E', marginBottom:'12px'}}><i className="fa fa-clock-o"></i> Reservas </h3>
                                <div className="players-grid">
                                    {reserves.map(p => (
                                        <div key={p.id} className="player-chip selected" style={{borderColor: '#FDE68A'}}>
                                            <div className="avatar-mini" style={{background:'#F59E0B'}}>{getInitials(p)}</div>
                                            {p.name} {p.lastName}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div style={{marginTop: '24px', borderTop: '1px solid #f1f5f9', paddingTop: '16px'}}>
                             <span className="players-section-title" style={{color:'var(--brand-primary)'}}>
                                <i className="fa fa-clock-o"></i> EN LISTA DE ESPERA ({available.length})
                            </span>
                            {available.length === 0 ? <p style={{fontStyle:'italic', color:'#999', fontSize:'0.9rem'}}>Nadie en espera.</p> : (
                                <div className="players-grid">
                                    {available.map(p => (
                                        <div key={p.id} className="player-chip">
                                            <div className="avatar-mini">{getInitials(p)}</div>
                                            {p.name} {p.lastName}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {matchDay?.location && (
                    <div className="content-card" style={{padding:0, overflow:'hidden'}}>
                        <iframe title="Match Location" className="map-frame" src={`https://maps.google.com/maps?q=${encodeURIComponent(matchDay.location)}&t=&z=13&ie=UTF8&iwloc=&output=embed`} allowFullScreen />
                        <div className="map-footer">
                            <div className="location-text"><i className="fa fa-map-marker" style={{color:'var(--brand-primary)'}}></i> {matchDay.location}</div>
                            <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(matchDay.location)}`} target="_blank" rel="noopener noreferrer" className="btn-gps">
                                <i className="fa fa-external-link"></i> Abrir GPS
                            </a>
                        </div>
                    </div>
                )}
            </div>
            {confirmRemovePlayer && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 1000,
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <div style={{
                        background: 'white', borderRadius: '16px', padding: '32px',
                        maxWidth: '400px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            width: '56px', height: '56px', borderRadius: '50%',
                            backgroundColor: '#FEE2E2', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}>
                            <i className="fa fa-user-times" style={{ fontSize: '1.5rem', color: '#EF4444' }} />
                        </div>

                        <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', color: '#1e293b' }}>
                            Quitar disponibilidad
                        </h3>
                        <p style={{ margin: '0 0 24px', color: '#64748B', fontSize: '0.9rem' }}>
                            ¿Segura que quieres quitar a{' '}
                            <strong>{confirmRemovePlayer.name} {confirmRemovePlayer.lastName}</strong>{' '}
                            de este partido?
                        </p>

                        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                            <button
                                onClick={() => setConfirmRemovePlayer(null)}
                                style={{
                                    padding: '10px 24px', borderRadius: '8px', border: '1px solid #E2E8F0',
                                    background: 'white', color: '#64748B', cursor: 'pointer',
                                    fontWeight: '600', fontSize: '0.9rem'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleRemovePlayerAvailability}
                                style={{
                                    padding: '10px 24px', borderRadius: '8px', border: 'none',
                                    background: '#EF4444', color: 'white', cursor: 'pointer',
                                    fontWeight: '600', fontSize: '0.9rem'
                                }}
                            >
                                Sí, quitar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showNotifyModal && (
                <NotifyModal
                    players={[
                        ...selected.map(p => ({ ...p, isReserve: false })),
                        ...reserves.map(p => ({ ...p, isReserve: true }))
                    ]}
                    onConfirm={(playerIds) => {
                        handleSendNotifications(playerIds);
                        setShowNotifyModal(false);
                    }}
                    onClose={() => setShowNotifyModal(false)}
                />
            )}
        </div>
    );
}